import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@/lib/api/client";
import { Icon as BrandIcon } from "@/lib/Icon";
import { IntakeFormField, focusIntakeField } from "../IntakeFormField";
import { PageHeader } from "@/components/app-shell";
import { applicableAnswers, questionError, questionVisible } from "./answers";
import { IntakeFields } from "./IntakeFields";
import { useMasterProductDraft, useMasterProductSubmit } from "./hooks";
import type {
  IntakeAnswer,
  IntakeAnswers,
  IntakeComponent,
  IntakeEnvelope,
  IntakePlan,
} from "./types";

const buttonClass =
  "min-h-44 px-15 py-12 type-body-md border border-line disabled:opacity-50";
const dateLabel = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : "Not yet completed";

export function MasterProductIntake({
  initialPlan,
  onCollected,
}: {
  initialPlan: IntakePlan;
  onCollected: (plan: IntakePlan) => void;
}) {
  const draft = useMasterProductDraft();
  const submit = useMasterProductSubmit();
  const [plan, setPlan] = useState(initialPlan);
  const [answers, setAnswers] = useState<IntakeEnvelope>(() =>
    Object.fromEntries(
      initialPlan.components.map((component) => [
        component.id,
        Object.fromEntries(
          Object.entries(component.answers).filter(
            ([key]) =>
              component.status === "complete" ||
              !component.definition.questions.some(
                (question) =>
                  question.key === key &&
                  ["signature", "acknowledgement"].includes(question.type),
              ),
          ),
        ),
      ]),
    ),
  );
  const [step, setStep] = useState(0);
  const [editingMaster, setEditingMaster] = useState(
    initialPlan.master.status === "missing" ||
      initialPlan.master.status === "annual_due" ||
      initialPlan.components.some(
        (component) =>
          component.kind === "master" && component.status === "missing",
      ),
  );
  const [confirmed, setConfirmed] = useState(false);
  const [serverMasterRequirement, setServerMasterRequirement] = useState<
    "confirmation" | "full" | null
  >(null);
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, Record<string, string>>
  >({});
  const [focusField, setFocusField] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadsPending, setUploadsPending] = useState(0);
  const revision = useRef(initialPlan.revision!);
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const answersRef = useRef(answers);
  const dirty = useRef(false);
  const submission = useRef<{
    fingerprint: string;
    body: Parameters<typeof submit.mutateAsync>[0];
  } | null>(null);
  const master = initialPlan.components.find(
    (component) => component.kind === "master",
  );
  const masterAnswers = master
    ? applicableAnswers(master.definition.questions, answers[master.id] ?? {})
    : {};
  const queue = useMemo(
    () =>
      initialPlan.components.filter(
        (component) =>
          component.status !== "complete" ||
          (component.kind === "master" &&
            (initialPlan.master.status !== "current" ||
              serverMasterRequirement !== null)),
      ),
    [
      initialPlan.components,
      initialPlan.master.status,
      serverMasterRequirement,
    ],
  );
  const component = queue[step];
  const reviewing = component?.kind === "master" && !editingMaster;
  const last = step >= queue.length - 1;
  const invalidQuestions =
    component && !reviewing
      ? component.definition.questions.filter((question) =>
          questionError(
            question,
            applicableAnswers(
              component.definition.questions,
              answers[component.id] ?? {},
              masterAnswers,
            ),
            masterAnswers,
          ),
        )
      : [];
  useEffect(() => {
    if (focusField && !busy) {
      focusIntakeField(focusField);
      setFocusField(null);
    }
  }, [focusField, step, busy]);

  function showError(cause: unknown) {
    if (cause instanceof ApiError && cause.status === 422) {
      const raw = (cause.details as { validation_errors?: unknown } | null)
        ?.validation_errors;
      if (raw && typeof raw === "object") {
        const safe: Record<string, Record<string, string>> = {};
        for (const [componentId, errors] of Object.entries(raw)) {
          if (!errors || typeof errors !== "object") continue;
          safe[componentId] = Object.fromEntries(
            Object.entries(errors).filter(
              (entry): entry is [string, string] =>
                typeof entry[1] === "string",
            ),
          );
        }
        setFieldErrors(safe);
        // The annual can become due for review while product answers are open.
        // A server validation requirement is authoritative even when we skipped
        // that annual component at mount. Keep the entered product answers.
        const masterErrors = master ? safe[master.id] : undefined;
        if (masterErrors && Object.keys(masterErrors).length > 0) {
          const confirmation = Boolean(masterErrors._confirmation);
          setServerMasterRequirement(confirmation ? "confirmation" : "full");
          setEditingMaster(!confirmation);
          setConfirmed(false);
        }
        const validationQueue = initialPlan.components.filter(
          (item) =>
            queue.some((queued) => queued.id === item.id) ||
            (item.kind === "master" &&
              masterErrors &&
              Object.keys(masterErrors).length > 0),
        );
        const first = validationQueue.findIndex(
          (item) => Object.keys(safe[item.id] ?? {}).length > 0,
        );
        if (first >= 0) {
          setStep(first);
          setAttempted(true);
          const key = Object.keys(safe[validationQueue[first].id])[0];
          setFocusField(
            key === "_confirmation"
              ? "intake-master-confirmation"
              : `intake-question-${key}`,
          );
        }
      }
    }
    if (cause instanceof ApiError && cause.status === 409) {
      setConflict(true);
      setError(
        "These answers were updated in another session. Your entries are still shown here. Reload the saved version before continuing.",
      );
    } else {
      setError(
        cause instanceof Error
          ? cause.message
          : "Your answers could not be saved. Please retry.",
      );
    }
  }

  function envelope(current: IntakeEnvelope): IntakeEnvelope {
    const currentMaster = master
      ? applicableAnswers(master.definition.questions, current[master.id] ?? {})
      : {};
    return Object.fromEntries(
      initialPlan.components.map((item) => [
        item.id,
        applicableAnswers(
          item.definition.questions,
          current[item.id] ?? {},
          currentMaster,
        ),
      ]),
    );
  }

  async function save(current: IntakeEnvelope) {
    const task = saveQueue.current
      .catch(() => undefined)
      .then(async () => {
        setSaving(true);
        try {
          const result = await draft.mutateAsync({
            session_id: initialPlan.session_id!,
            revision: revision.current,
            answers: envelope(current),
          });
          revision.current = result.revision!;
          setPlan(result);
          setSaved(true);
          return result;
        } finally {
          setSaving(false);
        }
      });
    saveQueue.current = task;
    return task;
  }

  // Drafts live on the authenticated server. Never cache medical answers in
  // localStorage, analytics, or URL parameters. Serialize saves by revision.
  useEffect(() => {
    answersRef.current = answers;
    if (!dirty.current || conflict || busy) return;
    const timer = window.setTimeout(() => {
      void save(answers).catch(showError);
    }, 1200);
    return () => window.clearTimeout(timer);
    // Mutation identities intentionally do not schedule saves after server echoes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, conflict, busy]);

  const update = (key: string, value: IntakeAnswer) => {
    if (!component) return;
    dirty.current = true;
    setSaved(false);
    if (!conflict) setError(null);
    if (component.kind === "master") setConfirmed(false);
    setFieldErrors((previous) => ({
      ...previous,
      [component.id]: Object.fromEntries(
        Object.entries(previous[component.id] ?? {}).filter(
          ([field]) => field !== key && field !== "_confirmation",
        ),
      ),
    }));
    setAnswers((previous) => ({
      ...previous,
      [component.id]: { ...previous[component.id], [key]: value },
    }));
  };

  async function next() {
    if (!component || busy || conflict || uploadsPending > 0) return;
    setAttempted(true);
    if (reviewing && !confirmed) {
      focusIntakeField("intake-master-confirmation");
      return;
    }
    if (invalidQuestions.length) {
      focusIntakeField(`intake-question-${invalidQuestions[0].key}`);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const payload = envelope(answersRef.current);
      const confirmation =
        confirmed && !editingMaster && initialPlan.master.submission_id
          ? {
              submission_id: initialPlan.master.submission_id,
              unchanged: true as const,
            }
          : undefined;
      const fingerprint = JSON.stringify({ payload, confirmation });
      // If a submit response is lost, retry the exact request/key. A new draft
      // revision here could invalidate an already committed submission.
      if (!last || submission.current?.fingerprint !== fingerprint)
        await save(answersRef.current);
      if (!last) {
        setStep((value) => value + 1);
        setAttempted(false);
        return;
      }
      if (submission.current?.fingerprint !== fingerprint)
        submission.current = {
          fingerprint,
          body: {
            session_id: initialPlan.session_id!,
            revision: revision.current,
            answers: payload,
            idempotency_key: crypto.randomUUID(),
            ...(confirmation ? { master_confirmation: confirmation } : {}),
          },
        };
      dirty.current = false;
      const result = await submit.mutateAsync(submission.current.body);
      revision.current = result.revision!;
      setPlan(result);
      if (result.collection_complete) onCollected(result);
      else
        setError(
          result.message ||
            "Some required answers are still missing. Please review your intake.",
        );
    } catch (cause) {
      showError(cause);
    } finally {
      setBusy(false);
    }
  }

  if (!component)
    return (
      <div role="alert" className="p-20 type-body-md">
        Your intake requirements could not be loaded. Please refresh to try
        again.
      </div>
    );
  return (
    <div className="ph-no-capture flex-1 flex flex-col">
      <PageHeader
        title={
          reviewing ? "Review your annual intake" : component.definition.title
        }
        description={
          component.kind === "master"
            ? reviewing
              ? "Review your saved health history and confirm what has changed."
              : initialPlan.master.status === "annual_due"
                ? "Your annual intake is due. Review and complete your full health history for the coming year."
                : "Complete your shared health history once. We’ll reuse it for your product intakes."
            : "We’ve reused your shared health history. These questions are specific to your treatment."
        }
      />
      <div className="flex-1 px-15 md:px-40 pt-20 md:pt-40 pb-30 max-w-[44rem] md:max-w-[64rem] mx-auto w-full flex flex-col gap-20">
        <div className="flex justify-between gap-12 type-body-sm text-ink-muted">
          <span>
            Step {step + 1} of {queue.length}
          </span>
          <span role="status">
            {saving
              ? "Saving…"
              : saved
                ? "Draft saved"
                : "Your answers are private"}
          </span>
        </div>
        {component.kind === "master" && plan.master.last_completed_at && (
          <p className="type-body-sm text-ink-muted">
            Annual intake completed {dateLabel(plan.master.last_completed_at)} ·
            Next annual intake {dateLabel(plan.master.annual_due_at)}
            {plan.master.last_confirmed_at
              ? ` · Last confirmed ${dateLabel(plan.master.last_confirmed_at)}`
              : ""}
          </p>
        )}
        {reviewing ? (
          <>
            <SavedAnswers
              component={component}
              answers={answers[component.id] ?? {}}
              masterAnswers={masterAnswers}
            />
            <IntakeFormField
              fieldId="intake-master-confirmation"
              module={{
                id: "annual-confirmation",
                label: "I reviewed my saved answers and nothing has changed.",
                mod_type: "agree_to_above",
                required: true,
                options: "",
              }}
              value={confirmed ? "Yes" : ""}
              disabled={busy || conflict}
              onChange={(value) => {
                setConfirmed(value === "Yes");
                setFieldErrors((previous) => ({
                  ...previous,
                  [component.id]: {},
                }));
              }}
              error={
                fieldErrors[component.id]?._confirmation ||
                (attempted && !confirmed
                  ? "Confirm your answers are unchanged, or update them below."
                  : null)
              }
            />
            <button
              type="button"
              className={`${buttonClass} self-start`}
              onClick={() => {
                setEditingMaster(true);
                setConfirmed(false);
                setAttempted(false);
              }}
            >
              Update my answers
            </button>
          </>
        ) : (
          <IntakeFields
            questions={component.definition.questions}
            answers={answers[component.id] ?? {}}
            masterAnswers={masterAnswers}
            onChange={update}
            attempted={attempted}
            disabled={busy || conflict || uploadsPending > 0}
            onUploadStart={() => setUploadsPending((count) => count + 1)}
            onUploadEnd={() =>
              setUploadsPending((count) => Math.max(0, count - 1))
            }
            serverErrors={fieldErrors[component.id]}
          />
        )}
        {plan.flags.length > 0 && (
          <div className="p-15 border border-line-subtle bg-sunken flex flex-col gap-8">
            <p className="type-body-md font-medium">
              For your clinician to review
            </p>
            {plan.flags.map((flag) => (
              <p key={flag.code} className="type-body-sm">
                {flag.message}
              </p>
            ))}
          </div>
        )}
        {error && (
          <div role="alert" className="type-body-sm text-[#C81E1E]">
            {error}
            {conflict && (
              <button
                type="button"
                className="block underline mt-10"
                onClick={() => window.location.reload()}
              >
                Reload saved version
              </button>
            )}
          </div>
        )}
        {!attempted && invalidQuestions.length > 0 && (
          <span className="type-label-xs text-ink-muted">
            {invalidQuestions.length}{" "}
            {invalidQuestions.length === 1 ? "question" : "questions"} left
          </span>
        )}
      </div>
      <div className="sticky bottom-50 md:bottom-0 inset-x-0 border-t border-line-subtle bg-[var(--surface-page)] px-15 py-15 md:px-40 md:py-20">
        {attempted && invalidQuestions.length > 0 && (
          <div
            role="alert"
            className="type-label-xs text-[#C81E1E] mb-8 max-w-[64rem] mx-auto"
          >
            {invalidQuestions.length}{" "}
            {invalidQuestions.length === 1
              ? "question needs"
              : "questions need"}{" "}
            attention — marked in red above
          </div>
        )}
        <div className="max-w-[64rem] mx-auto flex gap-12">
          {step > 0 && (
            <button
              type="button"
              className={buttonClass}
              disabled={busy || uploadsPending > 0}
              onClick={() => {
                setStep((value) => value - 1);
                setAttempted(false);
              }}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="w-full flex items-center justify-between h-50 px-15 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90 disabled:opacity-50"
            disabled={busy || conflict || uploadsPending > 0}
            onClick={() => {
              void next();
            }}
          >
            {uploadsPending > 0
              ? "Uploading documents…"
              : busy
                ? "Saving…"
                : "Continue"}
            <span
              aria-hidden
              className="inline-flex items-center justify-center w-8"
            >
              <BrandIcon name="Arrow" viewBox="0 0 10 12" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function SavedAnswers({
  component,
  answers,
  masterAnswers,
}: {
  component: IntakeComponent;
  answers: IntakeAnswers;
  masterAnswers: IntakeAnswers;
}) {
  return (
    <dl className="divide-y divide-line-subtle border border-line-subtle px-15">
      {component.definition.questions
        .filter((question) =>
          questionVisible(
            question,
            applicableAnswers(
              component.definition.questions,
              answers,
              masterAnswers,
            ),
            masterAnswers,
          ),
        )
        .map((question) => {
          const value = answers[question.key];
          const values = Array.isArray(value) ? value : [value];
          const label = values
            .map((item) =>
              item === undefined || item === null || item === ""
                ? "Not answered"
                : typeof item === "boolean"
                  ? item
                    ? "Yes"
                    : "No"
                  : (question.options?.find((option) => option.value === item)
                      ?.label ?? String(item)),
            )
            .join(", ");
          return (
            <div key={question.key} className="py-12">
              <dt className="type-body-sm text-ink-muted">{question.label}</dt>
              <dd className="type-body-md mt-5 whitespace-pre-wrap break-words">
                {label}
              </dd>
            </div>
          );
        })}
    </dl>
  );
}
